import React from 'react';
import { reportClientError } from './errorReporting.js';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
        this._resetAttempted = false;
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        console.error('ErrorBoundary caught:', error.message, info.componentStack);
        reportClientError(error, {
            kind: 'render',
            label: this.props.label,
            componentStack: info.componentStack,
        });

        if (!this._resetAttempted) {
            this._resetAttempted = true;
            // Notify the app so it can show a dialog for errors the component didn't handle
            window.dispatchEvent(new CustomEvent('app-render-error', {
                detail: { message: error.message || String(error), label: this.props.label }
            }));
            // Auto-reset so the section returns to its default state rather than
            // staying on the error screen (the component's own fetch-error dialog
            // already told the user what went wrong).
            setTimeout(() => this.setState({ hasError: false, error: null }), 0);
        }
    }

    reset() {
        this._resetAttempted = false;
        this.setState({ hasError: false, error: null });
    }

    render() {
        if (!this.state.hasError) return this.props.children;

        // First crash: render nothing for the one tick before auto-reset fires.
        if (!this._resetAttempted) return null;

        // Persistent crash (auto-reset didn't help): show the fallback.
        if (this.props.fallback) return this.props.fallback;

        const label = this.props.label ? ` (${this.props.label})` : '';
        return (
            <div className="flex flex-col items-center justify-center gap-3 p-8 text-center">
                <p className="text-red-600 font-medium">Something went wrong{label}</p>
                <p className="text-sm text-gray-500 max-w-sm">{this.state.error?.message}</p>
                <button
                    onClick={() => this.reset()}
                    className="btn-t btn-t-secondary"
                >
                    Try again
                </button>
            </div>
        );
    }
}

export default ErrorBoundary;